import {
  KnownHubDtoType,
  KnownOrderDtoCancellationType,
  KnownOrderDtoStatus,
  OrderDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { Send } from '../../../../ui/components/pure/icons/Send.icon';
import { ActionOpen } from '../../../../ui/components/pure/icons/ActionOpen.icon';
import { ActionCancel } from '../../../../ui/components/pure/icons/ActionCancel.icon';
import { ActionUndoCancel } from '../../../../ui/components/pure/icons/ActionUndoCancel.icon';
import { ActionRetrieve } from '../../../../ui/components/pure/icons/ActionRetrieve.icon';
import { ActionCollect } from '../../../../ui/components/pure/icons/ActionCollect.icon';
import { ActionMarkRetrieved } from '../../../../ui/components/pure/icons/ActionMarkRetrieved.icon';
import { ActionDelete } from '../../../../ui/components/pure/icons/ActionDelete.icon';
import { Switch } from '../../../../ui/components/pure/icons/Switch.icon';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { Add } from '../../../../ui/components/pure/icons/Add.icon';
import { BookingFlowType } from '../../../../ui/enums/booking-flow-type.enum';

export enum OrderDetailsActions {
  RESEND_NOTIFICATION = 'RESEND_NOTIFICATION',
  RESEND_NOTIFICATION_SUCCESS = 'RESEND_NOTIFICATION_SUCCESS',
  OPEN_LOCKER = 'OPEN_LOCKER',
  OPEN_LOCKER_SUCCESS = 'OPEN_LOCKER_SUCCESS',
  CANCEL_ORDER = 'CANCEL_ORDER',
  CANCEL_ORDER_SUCCESS = 'CANCEL_ORDER_SUCCESS',
  UNDO_CANCELLATION = 'UNDO_CANCELLATION',
  UNDO_CANCELLATION_SUCCESS = 'UNDO_CANCELLATION_SUCCESS',
  REQUEST_RETRIEVAL = 'REQUEST_RETRIEVAL',
  REQUEST_RETRIEVAL_SUCCESS = 'REQUEST_RETRIEVAL_SUCCESS',
  REQUEST_RETRIEVAL_FAILED = 'REQUEST_RETRIEVAL_FAILED',
  MARK_COLLECTED = 'MARK_COLLECTED',
  MARK_COLLECTED_SUCCESS = 'MARK_COLLECTED_SUCCESS',
  MARK_RETRIEVED = 'MARK_RETRIEVED',
  MARK_RETRIEVED_SUCCESS = 'MARK_RETRIEVED_SUCCESS',
  MARK_CANCELLED = 'MARK_CANCELLED',
  MARK_CANCELLED_SUCCESS = 'MARK_CANCELLED_SUCCESS',
  ARCHIVE_ORDER = 'ARCHIVE_ORDER',
  PORTAL_HANDOFF = 'PORTAL_HANDOFF',
  SWITCH_SPACE = 'SWITCH_SPACE',
  ADD_NOTE = 'ADD_NOTE',
  ADD_NOTE_SUCCESS = 'ADD_NOTE_SUCCESS'
}

function makeOrderDetailsActions(order: OrderDto, projectID: string): ActionsButtonItem<OrderDetailsActions>[] {
  // If the order is in a DeliveredOffline status, we don't want to show any actions
  if (order.status === KnownOrderDtoStatus.DeliveredOffline) {
    return [];
  }

  // Standard statuses are based on the permissions of the user and if the order flow is retail or inventory based
  let actions = [
    {
      title: 'Add note',
      Icon: Add,
      action: OrderDetailsActions.ADD_NOTE,
      permission: {
        action: Permissions.notes.create,
        projectID
      }
    },
    {
      title: 'Resend notification',
      Icon: Send,
      action: OrderDetailsActions.RESEND_NOTIFICATION,
      permission: {
        action: Permissions.deliveries.sendNotification,
        projectID
      }
    },
    {
      title: 'Switch space',
      Icon: Switch,
      action: OrderDetailsActions.SWITCH_SPACE,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Open Locker',
      Icon: ActionOpen,
      action: OrderDetailsActions.OPEN_LOCKER,
      permission: {
        action: Permissions.deliveryContainers.open,
        projectID
      }
    },
    {
      title: 'Cancel',
      Icon: ActionCancel,
      action: OrderDetailsActions.CANCEL_ORDER,
      permission: {
        action: Permissions.deliveries.cancel,
        projectID
      }
    },
    {
      title: 'Undo cancellation',
      Icon: ActionUndoCancel,
      action: OrderDetailsActions.UNDO_CANCELLATION,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Retrieve',
      Icon: ActionRetrieve,
      action: OrderDetailsActions.REQUEST_RETRIEVAL,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Mark collected',
      Icon: ActionCollect,
      action: OrderDetailsActions.MARK_COLLECTED,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Mark retrieved',
      Icon: ActionMarkRetrieved,
      action: OrderDetailsActions.MARK_RETRIEVED,
      permission: {
        action: Permissions.deliveries.update,
        projectID
      }
    },
    {
      title: 'Mark cancelled',
      Icon: ActionCancel,
      action: OrderDetailsActions.MARK_CANCELLED,
      permission: {
        action: Permissions.deliveries.cancel,
        projectID
      }
    },
    {
      title: 'Archive order',
      Icon: ActionDelete,
      action: OrderDetailsActions.ARCHIVE_ORDER,
      permission: {
        action: Permissions.deliveries.delete,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    }
  ];

  return actions;
}

function makeValidateAction(order: OrderDto, bookingFlowType: BookingFlowType) {
  return function validateAction(action: OrderDetailsActions) {
    const isRetailFlow = bookingFlowType === BookingFlowType.RETAIL;
    // The actions affected by the Hub type are only available if the Order has already been delivered, so we can assume
    // that we will know the Hub type in that case.
    const isExternalHub =
      order.bookedIntoHub?.type === KnownHubDtoType.LockersHubExternal ||
      order.hub?.type === KnownHubDtoType.LockersHubExternal;
    const isLockersHub =
      order.hub?.type === KnownHubDtoType.LockersHub || order.hub?.type === KnownHubDtoType.LockersHubExternal;
    const isSpacesHub = !isLockersHub;

    switch (action) {
      case OrderDetailsActions.ADD_NOTE:
        return true;
      case OrderDetailsActions.RESEND_NOTIFICATION:
        // There is nobody to notify until a recipient is attached
        if (!order.customer) {
          return false;
        }
        switch (order.status) {
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
            return true;
          default:
            return false;
        }
      case OrderDetailsActions.OPEN_LOCKER:
        // Only allowed for internal lockers hubs
        if (!isLockersHub || isExternalHub) {
          return false;
        }
        switch (order.status) {
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
          case KnownOrderDtoStatus.RetrievalRequired:
          case KnownOrderDtoStatus.Retrieved:
          case KnownOrderDtoStatus.Collected:
            return true;
          case KnownOrderDtoStatus.Cancelled:
            switch (order.cancellationType) {
              case KnownOrderDtoCancellationType.CustomerServiceForced:
                return true;
              default:
                return false;
            }
          default:
            return false;
        }
      case OrderDetailsActions.CANCEL_ORDER:
        switch (order.status) {
          case KnownOrderDtoStatus.Booked:
            return true;
          default:
            return false;
        }
      case OrderDetailsActions.UNDO_CANCELLATION:
        switch (order.status) {
          case KnownOrderDtoStatus.Cancelled:
            switch (order.cancellationType) {
              case KnownOrderDtoCancellationType.Undelivered:
                return true;
              default:
                return false;
            }
          default:
            return false;
        }
      case OrderDetailsActions.REQUEST_RETRIEVAL:
        // Only allowed for retail flow orders
        if (!isRetailFlow) {
          return false;
        }
        if (!isLockersHub) {
          return false;
        }
        // Only allowed from Delivered or Overdue statuses
        switch (order.status) {
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
            return true;
          default:
            return false;
        }
      case OrderDetailsActions.MARK_COLLECTED:
        switch (order.status) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
            return !isExternalHub;
          case KnownOrderDtoStatus.Retrieved:
            return true;
          case KnownOrderDtoStatus.Cancelled:
            switch (order.cancellationType) {
              case KnownOrderDtoCancellationType.CustomerServiceForced:
                return true;
              default:
                return false;
            }
          default:
            return false;
        }
      case OrderDetailsActions.MARK_RETRIEVED:
        // Hide mark retrieved action for inventory flow projects
        if (!isRetailFlow) {
          return false;
        }
        switch (order.status) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
          case KnownOrderDtoStatus.RetrievalRequired:
            return !isExternalHub;
          case KnownOrderDtoStatus.Collected:
            return true;
          case KnownOrderDtoStatus.Cancelled:
            switch (order.cancellationType) {
              case KnownOrderDtoCancellationType.CustomerServiceForced:
                return true;
              default:
                return false;
            }
          default:
            return false;
        }
      case OrderDetailsActions.MARK_CANCELLED:
        switch (order.status) {
          // Moving to a final state from an intermediate state is only allowed on internal hubs
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
          case KnownOrderDtoStatus.RetrievalRequired:
            return !isExternalHub;
          case KnownOrderDtoStatus.Collected:
          case KnownOrderDtoStatus.Retrieved:
            return true;
          default:
            return false;
        }
      case OrderDetailsActions.SWITCH_SPACE:
        // You can only switch spaces if the hub is a spaces hub
        if (!isSpacesHub) return false;
        // Only allowed from Delivered or Overdue statuses
        switch (order.status) {
          case KnownOrderDtoStatus.Delivered:
          case KnownOrderDtoStatus.Overdue:
            return true;
          default:
            return false;
        }
      default:
        return true;
    }
  };
}

export function makeDefineOrderDetailsActions(order: OrderDto, projectID: string, bookingFlowType: BookingFlowType) {
  return makeDefineActions(
    makeOrderDetailsActions(order, projectID),
    undefined,
    makeValidateAction(order, bookingFlowType)
  );
}
